import React, { useState } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { formatCompactNumber, formatPercentage } from '@/lib/utils/formatNumber';
import { ExpandedPostsPanel } from '@/domains/content';
import type { DeliverableAggregate } from '../../types/deliverable.types';
import { formatContentTypes } from '../../services/deliverable.service';

export interface DeliverableRowProps {
  aggregate: DeliverableAggregate;
  onAddPost: (campaignInfluencerId: string, influencerName: string) => void;
}

export const DeliverableRow: React.FC<DeliverableRowProps> = ({ aggregate, onAddPost }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasContent = aggregate.post_count > 0;

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} asChild>
      <>
        <TableRow className="group">
          <TableCell>
            <div className="flex items-center gap-3">
              {hasContent && (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              )}
              <Avatar className="h-9 w-9 flex-shrink-0">
                <AvatarImage src={aggregate.influencer_avatar || undefined} />
                <AvatarFallback>
                  {aggregate.influencer_name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="font-medium">{aggregate.influencer_name}</div>
                {aggregate.influencer_handle ? (
                  <a
                    href={`https://www.instagram.com/${aggregate.influencer_handle.replace(/^@/, "")}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm text-muted-foreground hover:text-purple-600 hover:underline transition-colors block truncate"
                  >
                    @{aggregate.influencer_handle.replace(/^@/, "")}
                  </a>
                ) : (
                  <div className="text-sm text-muted-foreground">-</div>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell className="text-center">
            <div className="flex flex-wrap justify-center gap-1">
              {aggregate.post_count > 0 ? (
                formatContentTypes(aggregate.content_types, aggregate.post_count)
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatCompactNumber(aggregate.total_views)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatCompactNumber(aggregate.total_likes)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatCompactNumber(aggregate.total_comments)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatCompactNumber(aggregate.total_shares)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatCompactNumber(aggregate.total_engagement)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatPercentage(aggregate.er_followers)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatPercentage(aggregate.er_views)}
          </TableCell>
          <TableCell className="text-center font-medium">
            {formatPercentage(aggregate.view_rate)}
          </TableCell>
          <TableCell>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAddPost(aggregate.campaign_influencer_id, aggregate.influencer_name)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </TableCell>
        </TableRow>
        
        {hasContent && (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={11} className="p-0">
              <CollapsibleContent>
                <ExpandedPostsPanel
                  campaignInfluencerId={aggregate.campaign_influencer_id}
                  isExpanded={isExpanded}
                  influencerFollowers={aggregate.influencer_followers}
                />
              </CollapsibleContent>
            </TableCell>
          </TableRow>
        )}
      </>
    </Collapsible>
  );
};
